import React, { useEffect, useState } from "react";
import styled from "styled-components";
import SearchIcon from "@material-ui/icons/Search";
import { NetworkStatus } from "apollo-client";
import produce from "immer";
import { useInView } from "react-intersection-observer";
import { ChevronDown, CircleX } from "@sonder/ui/source/icons";
import { Dropdown } from "@sonder/ui/source/styled/components/Dropdown";
import { Button } from "@sonder/ui/source/styled/fragments/Button";
import { GenericDropdownItem } from "@sonder/ui/source/styled/fragments/GenericDropdownItem";
import {
  FilterButtonList,
  FilterDropdownContent,
  FilterEmpty,
  FilterFooter,
} from "@sonder/ui/source/styled/fragments/GenericFilters";
import { Input } from "@sonder/ui/source/styled/fragments/Input";
import { InputWithIcons } from "@sonder/ui/source/styled/fragments/InputWithIcons";
import { Link } from "@sonder/ui/source/styled/fragments/Link";
import { colors } from "@sonder/ui/source/styled/theme";
import { useSegment } from "analytics/segment";
import {
  SearchForTaskContextsItem,
  SearchForTaskContextsVariables,
  useSearchForTaskContexts,
} from "core/queries/SearchForTaskContexts";
import { TaskContextSearchLoader } from "tasks/components/TaskContextSearch.loader";
import {
  TaskContextSubjectTypeEnum,
  TaskLocationPlaceEnum,
  TypeEnum,
} from "typings/globalGQLTypes";

const StyledIntersectionObserver = styled.div`
  padding: 1px;
`;

const StyledGenericDropdownItem = styled(GenericDropdownItem)`
  box-sizing: border-box;
  height: inherit;
  padding: 0.25rem 0.5rem;
`;

const StyledDropdownItemContent = styled.div`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
`;

const StyledText = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
`;

const StyledTitle = styled(StyledText)`
  color: ${colors.black};
  line-height: 1.71rem;
`;

const StyledSubtitle = styled(StyledText)`
  color: ${colors.gray6};
  line-height: 1.429rem;
`;

const StyledFilterButtonList = styled(FilterButtonList)`
  max-width: inherit;
`;

const StyledFilterFooter = styled(FilterFooter)`
  ${Link} {
    color: ${colors.blue7};
  }
`;

const StyledError = styled.p`
  color: ${colors.redD};
`;

const StyledClearDropdownTrigger = styled(Button)`
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translate3d(0, -50%, 0);
`;

const StyledDropdownTriggerWrapper = styled.div`
  background: ${colors.white};
  position: relative;

  ${Button},
  ${GenericDropdownItem} {
    border: 1px solid ${colors.grayE};
    border-radius: 0.3rem;
  }

  ${StyledClearDropdownTrigger} {
    border: 0;
    border-radius: 50%;
  }
`;
interface TaskContextSearchItemProps {
  item: SearchForTaskContextsItem;
  onClick?: () => void;
}

const TaskContextSearchItem = ({
  item,
  onClick,
}: TaskContextSearchItemProps) => {
  const { title, subtitle, description } = item;
  return (
    <StyledGenericDropdownItem onClick={() => (onClick ? onClick() : null)}>
      <StyledDropdownItemContent>
        <StyledTitle>{title}</StyledTitle>
        <StyledSubtitle>{subtitle}</StyledSubtitle>
        <StyledSubtitle>{description}</StyledSubtitle>
      </StyledDropdownItemContent>
    </StyledGenericDropdownItem>
  );
};

interface Props {
  locationPlaces: TaskLocationPlaceEnum[];
  onChange?: (context: SearchForTaskContextsItem | null) => void;
  onGuidedFlowOpen?: () => void;
  selectedContext: SearchForTaskContextsItem | null;
  subjectType: TaskContextSubjectTypeEnum;
  taskType: TypeEnum;
}

export const TaskContextSearch = ({
  locationPlaces = [],
  onChange,
  onGuidedFlowOpen,
  selectedContext,
  subjectType,
  taskType,
}: Props) => {
  const [lastItemRef, isLastItemInView] = useInView();
  const [searchTerm, setSearchTerm] = useState("");

  const getVariables = ({
    cursor,
  }: {
    cursor: string;
  }): SearchForTaskContextsVariables => {
    return {
      searchTerm,
      filters: {
        taskContext: {
          taskTypes: [taskType],
          taskLocationPlaces: locationPlaces,
          subjectTypes: [subjectType],
        },
      },
      pagination: {
        cursor,
        limit: 25,
      },
    };
  };

  const onClear = () => {
    setSearchTerm("");
    if (onChange) onChange(null);
  };

  const isSearchTermEmpty = searchTerm === "";

  const {
    data: searchData,
    loading,
    error,
    fetchMore,
    networkStatus,
  } = useSearchForTaskContexts({
    variables: getVariables({ cursor: "0" }),
    skip: isSearchTermEmpty,
  });

  const hasMore = searchData?.searchWithMetadata?.hasMore;
  const isFetchingMore = networkStatus === NetworkStatus.fetchMore;

  const fetchMoreContext = () => {
    const variables = getVariables({
      cursor: (
        parseInt(searchData?.searchWithMetadata.cursor ?? "0", 10) + 1
      ).toString(),
    });
    fetchMore({
      variables,
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;

        const moreData = produce(prev, draft => {
          draft.searchWithMetadata = {
            ...fetchMoreResult.searchWithMetadata,
            items: [
              ...prev.searchWithMetadata.items,
              ...fetchMoreResult.searchWithMetadata.items,
            ],
          };
        });

        return moreData;
      },
    });
  };

  useEffect(() => {
    if (hasMore && isLastItemInView && !isFetchingMore) fetchMoreContext();
  }, [hasMore, isLastItemInView, isFetchingMore]);

  const items = searchData?.searchWithMetadata?.items || [];

  const isEmpty = items.length === 0;
  const isEmptyAndLoading = isEmpty && loading;
  const isEmptyAndSearched = !loading && isEmpty && !isSearchTermEmpty;

  const { track } = useSegment();

  return (
    <Dropdown
      trigger={
        selectedContext ? (
          <StyledDropdownTriggerWrapper>
            <TaskContextSearchItem item={selectedContext} />
            <StyledClearDropdownTrigger
              iconOnly
              onClick={e => {
                onClear();
                e.stopPropagation();
              }}
            >
              <CircleX />
            </StyledClearDropdownTrigger>
          </StyledDropdownTriggerWrapper>
        ) : (
          <Button
            fullWidth
            iconRight
            type="button"
            onClick={() =>
              track({
                eventName: "task_context_search",
                action: "click",
                element: "search_button",
              })
            }
          >
            Search contexts
            <ChevronDown />
          </Button>
        )
      }
    >
      {({ isOpen: isPortalOpen, closePortal }) =>
        isPortalOpen && (
          <FilterDropdownContent align="left">
            <InputWithIcons childLeft childRight>
              <SearchIcon />
              <Input
                autoFocus
                placeholder="Search contexts"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </InputWithIcons>

            {error && error.message && (
              <StyledError>There was an error: {error.message}</StyledError>
            )}
            {isEmptyAndLoading && <FilterEmpty>Searching...</FilterEmpty>}
            {isEmptyAndSearched && (
              <FilterEmpty>{`No Results for ${searchTerm}.`}</FilterEmpty>
            )}
            {!isEmpty && !isEmptyAndSearched && (
              <>
                <StyledFilterButtonList>
                  {items.map(item => {
                    return (
                      <TaskContextSearchItem
                        item={item}
                        key={item.id}
                        onClick={() => {
                          track({
                            eventName: "task_context_search",
                            action: "click",
                            element: "search_item",
                            props: { taskContextId: item.id },
                          });
                          if (onChange) onChange(item);

                          closePortal();
                        }}
                      />
                    );
                  })}
                  {hasMore && <StyledIntersectionObserver ref={lastItemRef} />}
                </StyledFilterButtonList>
                {isFetchingMore && <TaskContextSearchLoader />}
              </>
            )}
            <StyledFilterFooter>
              <Link
                onClick={() => {
                  track({
                    eventName: "task_context_search",
                    action: "click",
                    element: "guided_flow_button",
                  });
                  if (onGuidedFlowOpen) onGuidedFlowOpen();
                }}
              >
                Filter by location/category
              </Link>
            </StyledFilterFooter>
          </FilterDropdownContent>
        )
      }
    </Dropdown>
  );
};
